import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { imgW, srcSet, imgFallback, imgT } from "@/utils/img";
import { Heart, Minus, Plus, Truck, ShieldCheck, RefreshCcw, Star, ZoomIn, X } from "lucide-react";
import api from "@/api";
import { useStore, formatINR, formatDateRange } from "@/store/StoreContext";
import Seo from "@/components/store/Seo";
import ProductCard from "@/components/store/ProductCard";
import FootwearLoader from "@/components/store/FootwearLoader";
import { toast } from "sonner";

function SizeChartModal({ open, onClose, chart, pdf }) {
  const [view, setView] = useState(pdf ? "pdf" : "table");
  if (!open) return null;
  const columns = chart?.columns || ["UK/IND", "EU", "Foot Length (cm)"];
  const rows = chart?.rows || [];
  const pdfUrl = pdf ? (pdf.url.startsWith("http") ? pdf.url : `${process.env.REACT_APP_BACKEND_URL}${pdf.url}`) : null;
  const previewUrl = pdf ? `${process.env.REACT_APP_BACKEND_URL}/api/size-chart-preview?url=${encodeURIComponent(pdf.url)}` : null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4" role="dialog" aria-modal="true" data-testid="size-chart-modal">
      <div className="absolute inset-0 bg-[#111111]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl p-4 sm:p-8 shadow-2xl tq-fade-up max-h-[94vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">Size Guide</h3>
          <button onClick={onClose} aria-label="Close size guide" data-testid="size-chart-close" className="w-9 h-9 flex items-center justify-center border border-[#EAEAEA] hover:border-[#111111]"><X size={16} /></button>
        </div>
        {pdfUrl && rows.length > 0 && (
          <div className="flex gap-2 mb-4">
            {[["table", "Size Table"], ["pdf", "Chart Image"]].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`size-chart-view-${v}`}
                className={`flex-1 py-2 text-[11px] font-bold uppercase tracking-wider border ${view === v ? "bg-[#111111] text-white border-[#111111]" : "border-[#EAEAEA]"}`}>{l}</button>
            ))}
          </div>
        )}
        {pdfUrl && view === "pdf" ? (
          <div className="overflow-auto border border-[#EAEAEA] bg-white" data-testid="size-chart-preview">
            <img src={previewUrl} alt={pdf.name || "Size chart"} loading="lazy" data-testid="size-chart-image"
              className="w-full min-w-[440px] sm:min-w-0 h-auto block" />
          </div>
        ) : (
          <>
            <p className="text-xs text-[#C59B27] mb-4">Measure your foot from heel to longest toe and match it below. Between sizes? Size up for pointed styles.</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#111111] text-[#FFFFFF]">
                  {columns.map((c) => <th key={c} className="py-2.5 px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-left">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-[#FAFAFA]" : "bg-white"}>
                    {r.map((cell, j) => <td key={j} className="py-2.5 px-3 border-b border-[#EAEAEA]">{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

function Gallery({ images, name }) {
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const imgs = images || [];
  if (!imgs.length) return <div className="aspect-[3/4] bg-[#FAFAFA]" />;
  return (
    <div>
      <div className="relative aspect-[3/4] bg-[#FAFAFA] overflow-hidden group cursor-zoom-in" onClick={() => setZoom(true)} data-testid="product-gallery">
        <img src={imgW(imgs[idx]?.url, 1100)} srcSet={srcSet(imgs[idx]?.url)} sizes="(max-width:1024px) 100vw, 50vw" alt={imgs[idx]?.alt || name} className="w-full h-full" style={imgT(imgs[idx]?.transform)} fetchPriority="high" onError={imgFallback} />
        <span className="absolute bottom-3 right-3 bg-white/90 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn size={16} /></span>
      </div>
      {imgs.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
          {imgs.map((im, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`gallery-thumb-${i}`}
              className={`w-16 h-20 shrink-0 overflow-hidden border-2 transition-colors ${i === idx ? "border-[#C59B27]" : "border-transparent"}`}>
              <img src={im.url} alt={im.alt || `${name} view ${i + 1}`} className="w-full h-full" style={imgT(im.transform)} loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {zoom && (
        <div className="fixed inset-0 z-[60] bg-[#111111]/95 flex items-center justify-center p-4" onClick={() => setZoom(false)} data-testid="gallery-zoom">
          <img src={imgs[idx]?.url} alt={name} className="max-h-[90vh] max-w-full object-contain" style={{ transform: imgs[idx]?.transform?.rot ? `rotate(${imgs[idx].transform.rot}deg)` : undefined }} />
        </div>
      )}
    </div>
  );
}

function PincodeChecker({ productId, onResult }) {
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pincode/check", { pin, product_id: productId });
      setResult(data);
      if (onResult) onResult(data);
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#EAEAEA] p-4 bg-white" data-testid="pincode-checker">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#666666] mb-3 flex items-center gap-2"><Truck size={14} /> Delivery & COD Check</p>
      <form onSubmit={check} className="flex gap-2">
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Enter PIN code" inputMode="numeric" data-testid="pincode-input"
          className="tq-input !py-2.5 flex-1" />
        <button type="submit" disabled={busy || pin.length !== 6} data-testid="pincode-check-btn" className="tq-btn-outline !px-4 !py-2.5">
          {busy ? "…" : "Check"}
        </button>
      </form>
      {result && (
        <div className="mt-3 text-sm space-y-1" data-testid="pincode-result">
          <p className="text-[#1A3C34] font-medium">Delivery available to {result.pin} · <strong>Get it by {formatDateRange(result)}</strong></p>
          <p className={result.cod_available ? "text-[#1A3C34]" : "text-[#8B1E21]"}>
            {result.cod_available ? "✓ Cash on Delivery available" : "✕ COD not available for this PIN"}
          </p>
        </div>
      )}
    </div>
  );
}

function NotifyMe({ product, size, variantId }) {
  const [contact, setContact] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const isEmail = contact.includes("@");
    try {
      const { data } = await api.post("/notify-me", {
        product_id: product.id, variant_id: variantId, size,
        email: isEmail ? contact : "", phone: isEmail ? "" : contact,
      });
      toast.success(data.message);
      setDone(true);
    } catch (err) { toast.error(err.friendlyMessage); }
  };
  if (done) return <p className="text-sm text-[#1A3C34] font-medium" data-testid="notify-success">✓ We'll notify you when size {size} is back.</p>;
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="notify-form">
      <input value={contact} onChange={(e) => setContact(e.target.value)} required
        placeholder="Email or mobile" data-testid="notify-input" className="tq-input !py-2.5 flex-1" />
      <button type="submit" data-testid="notify-submit" className="tq-btn-dark !px-4 !py-2.5">Notify Me</button>
    </form>
  );
}

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlistIds, settings } = useStore();
  const [data, setData] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("description");
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", body: "", name: "" });
  const [reviewBusy, setReviewBusy] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const [pinEstimate, setPinEstimate] = useState(null);

  useEffect(() => {
    setData(null); setVariant(null); setQty(1); setPinEstimate(null); setChartOpen(false);
    api.get(`/products/${slug}`).then(({ data }) => {
      setData(data);
      try {
        const rv = JSON.parse(localStorage.getItem("tq_recent") || "[]").filter((s) => s !== slug);
        localStorage.setItem("tq_recent", JSON.stringify([slug, ...rv].slice(0, 12)));
      } catch (e) { /* silent */ }
    }).catch(() => navigate("/404", { replace: true }));
  }, [slug, navigate]);

  const sizes = useMemo(() => {
    const map = {};
    (data?.product?.variants || []).forEach((v) => {
      if (v.enabled === false) return;
      if (!map[v.size]) map[v.size] = [];
      map[v.size].push(v);
    });
    return Object.keys(map).sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));
  }, [data]);

  if (!data) {
    return <div className="tq-container py-12" data-testid="product-loading"><FootwearLoader /></div>;
  }
  const { product: p, related, reviews, estimate, size_chart_pdf } = data;
  const price = variant?.price ?? p.price;
  const mrp = variant?.mrp ?? p.mrp;
  const discount = mrp > price ? Math.round((1 - price / mrp) * 100) : 0;
  const selectedVariants = variant ? [variant] : [];
  const sizeStock = (s) => (p.variants || []).filter((v) => v.size === s && v.enabled !== false).reduce((a, v) => a + v.stock, 0);
  const wished = wishlistIds.includes(p.id);
  const mii = settings?.made_in_india || {};
  const chart = (p.size_chart_rows || []).length ? { columns: settings?.size_chart?.columns, rows: p.size_chart_rows } : settings?.size_chart;

  const buy = async (buyNow) => {
    if (!variant) { toast.info("Please select a size first"); return; }
    try {
      await addToCart(p.id, variant.id, qty);
      if (buyNow) navigate("/checkout");
    } catch (err) { toast.error(err.friendlyMessage); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (reviewBusy) return;
    setReviewBusy(true);
    try {
      const { data } = await api.post(`/products/${slug}/reviews`, reviewForm);
      toast.success(data.message);
      setReviewForm({ rating: 5, title: "", body: "", name: "" });
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setReviewBusy(false); }
  };

  return (
    <div data-testid="product-page" className="tq-container py-8 sm:py-12 pb-28 md:pb-12">
      <Seo title={p.seo?.title?.replace(" | TARISHQ FASHION", "") || p.name} description={p.seo?.description || p.short_description}
        image={p.images?.[0]?.url}
        schema={{
          "@context": "https://schema.org", "@type": "Product", name: p.name,
          image: (p.images || []).map((i) => i.url), description: p.short_description,
          brand: { "@type": "Brand", name: "TARISHQ" }, sku: p.sku,
          offers: { "@type": "Offer", priceCurrency: "INR", price: price,
            availability: (p.variants || []).some((v) => v.stock > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
        }} />
      <nav className="text-xs text-[#8E8E93] uppercase tracking-wider mb-6" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-[#A8821B]">Home</Link> <span className="mx-1">/</span>
        {p.category_slugs?.[0] && <><Link to={`/collections/${p.category_slugs[0]}`} className="hover:text-[#A8821B] capitalize">{p.category_slugs[0].replace(/-/g, " ")}</Link> <span className="mx-1">/</span></>}
        <span className="text-[#111111]">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
        <Gallery images={p.images} name={p.name} />
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            {(p.badges || []).map((b) => <span key={b} className="text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-1 bg-[#FAFAFA] border border-[#A8821B] text-[#C59B27]">{b.replace("-", " ")}</span>)}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">{p.name}</h1>
          {p.rating_count > 0 && (
            <p className="flex items-center gap-1.5 text-sm mt-2 text-[#C59B27]">
              <Star size={14} className="fill-[#C59B27] text-[#C59B27]" /> {p.rating_avg} · {p.rating_count} reviews
            </p>
          )}
          <div className="flex items-baseline gap-3 mt-4">
            <span className="text-2xl font-semibold">{formatINR(price)}</span>
            {mrp > price && <><span className="text-lg text-[#8E8E93] line-through">{formatINR(mrp)}</span>
              <span className="text-sm font-semibold text-[#1A3C34]">{discount}% OFF</span></>}
          </div>
          <p className="text-xs text-[#8E8E93] mt-1">Inclusive of all taxes</p>

          <div className="mt-7">
            <div className="flex items-center justify-between mb-3">
              <p className="tq-overline !text-[#111111]">Select Size (UK/IND)</p>
              <button type="button" onClick={() => setChartOpen(true)} data-testid="size-guide-btn" className="text-xs text-[#C59B27] underline underline-offset-2 hover:text-[#A8821B] transition-colors">Size Guide</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {sizes.map((s) => {
                const stock = sizeStock(s);
                const v = (p.variants || []).find((v) => v.size === s && v.enabled !== false);
                const selected = variant?.size === s;
                return (
                  <button key={s} onClick={() => stock > 0 && setVariant(v)} disabled={stock === 0}
                    data-testid={`size-btn-${s}`}
                    className={`w-12 h-12 border text-sm font-medium transition-all ${selected ? "bg-[#111111] text-[#FFFFFF] border-[#111111]" : stock === 0 ? "border-[#EAEAEA] text-[#c9c2b5] line-through cursor-not-allowed" : "border-[#A8821B]/60 hover:border-[#111111]"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
            {variant && variant.stock <= 3 && variant.stock > 0 && (
              <p className="text-xs text-[#8B1E21] mt-2 font-medium" data-testid="low-stock-alert">Only {variant.stock} left in size {variant.size}</p>
            )}
            {variant && variant.stock === 0 && <NotifyMe product={p} size={variant.size} variantId={variant.id} />}
          </div>

          <div className="flex items-center gap-4 mt-6">
            <p className="tq-overline !text-[#111111]">Qty</p>
            <div className="flex items-center border border-[#EAEAEA]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease" data-testid="qty-minus" className="w-9 h-9 flex items-center justify-center"><Minus size={14} /></button>
              <span className="w-10 text-center text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(10, qty + 1))} aria-label="Increase" data-testid="qty-plus" className="w-9 h-9 flex items-center justify-center"><Plus size={14} /></button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button onClick={() => buy(false)} disabled={!variant || variant.stock === 0} data-testid="add-to-cart-btn" className="tq-btn-outline">
              Add to Bag
            </button>
            <button onClick={() => buy(true)} disabled={!variant || variant.stock === 0} data-testid="buy-now-btn" className="tq-btn-dark">
              Buy Now
            </button>
          </div>

          <div className="fixed bottom-14 inset-x-0 z-40 md:hidden bg-white/95 backdrop-blur border-t border-[#EAEAEA] px-4 py-2.5 flex gap-2" data-testid="mobile-sticky-cta">
            <button type="button" onClick={() => document.querySelector('[data-testid^="size-btn-"]')?.scrollIntoView({ behavior: "smooth", block: "center" })} data-testid="sticky-select-size" className="tq-btn-outline flex-1 !py-3">
              {variant ? `Size ${variant.size}` : "Select Size"}
            </button>
            <button type="button" onClick={() => buy(false)} disabled={!variant || variant.stock === 0} data-testid="sticky-add-to-bag" className="tq-btn-dark flex-1 !py-3">
              Add to Bag
            </button>
          </div>
          <button onClick={() => toggleWishlist(p.id)} data-testid="pdp-wishlist-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] py-3 border border-transparent hover:border-[#EAEAEA] transition-colors">
            <Heart size={15} className={wished ? "fill-[#8B1E21] text-[#8B1E21]" : ""} /> {wished ? "Saved to Wishlist" : "Add to Wishlist"}
          </button>

          <div className="mt-5 bg-[#FAFAFA] p-4 text-sm flex items-center gap-3" data-testid="delivery-estimate">
            <Truck size={18} className="text-[#A8821B] shrink-0" />
            <span>Estimated delivery: <strong>{formatDateRange(pinEstimate || estimate)}</strong>{pinEstimate ? ` to PIN ${pinEstimate.pin}` : " · Enter PIN for exact date"}</span>
          </div>
          <div className="mt-3"><PincodeChecker productId={p.id} onResult={setPinEstimate} /></div>

          <div className="grid grid-cols-3 gap-3 mt-6 text-center text-[11px] text-[#666666]">
            <div className="p-3 border border-[#EAEAEA]"><ShieldCheck size={18} className="mx-auto mb-1.5 text-[#A8821B]" />Secure Payment</div>
            <div className="p-3 border border-[#EAEAEA]"><RefreshCcw size={18} className="mx-auto mb-1.5 text-[#A8821B]" />7-Day Exchange</div>
            <div className="p-3 border border-[#EAEAEA]"><Truck size={18} className="mx-auto mb-1.5 text-[#A8821B]" />Pan-India Delivery</div>
          </div>
          {mii.enabled && mii.show_product !== false && (
            <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[#C59B27]" data-testid="made-in-india-product">{mii.text || "Proudly Made in India"}</p>
          )}
        </div>
      </div>

      <div className="mt-14 border-t border-[#EAEAEA]">
        <div className="flex gap-6 overflow-x-auto no-scrollbar border-b border-[#EAEAEA]">
          {["description", "details", "shipping", "reviews"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`pdp-tab-${t}`}
              className={`py-4 text-xs font-semibold uppercase tracking-[0.2em] whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-[#C59B27] text-[#111111]" : "border-transparent text-[#8E8E93] hover:text-[#111111]"}`}>
              {t === "reviews" ? `Reviews (${reviews.length})` : t === "shipping" ? "Shipping & Returns" : t}
            </button>
          ))}
        </div>
        <div className="py-8 max-w-3xl text-sm leading-relaxed text-[#444]">
          {tab === "description" && (
            <div>
              {p.highlights?.length > 0 && (
                <ul className="space-y-2 mb-6">
                  {p.highlights.map((h, i) => <li key={i} className="flex gap-2"><span className="text-[#C59B27]">◆</span>{h}</li>)}
                </ul>
              )}
              <p>{p.description}</p>
            </div>
          )}
          {tab === "details" && (
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[["Material", p.material], ["Heel Type", p.heel_type], ["Heel Height", p.heel_height],
                ["Color", p.color], ["Occasion", (p.occasion || []).join(", ")], ["Care", p.care]]
                .filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}><dt className="tq-overline !text-[10px] mb-1">{k}</dt><dd className="text-sm">{v}</dd></div>
                ))}
            </dl>
          )}
          {tab === "shipping" && (
            <div className="space-y-4">
              <p>{p.shipping_info || "Dispatched within 24–48 hours."}</p>
              <p>Easy 7-day size exchange on unused footwear in original packaging. COD available on eligible orders (small handling fee applies).</p>
            </div>
          )}
          {tab === "reviews" && (
            <div>
              {reviews.length === 0 ? (
                <p className="text-[#666666]" data-testid="no-reviews">No reviews yet. Be the first to share your experience.</p>
              ) : (
                <ul className="space-y-6 mb-10">
                  {reviews.map((r) => (
                    <li key={r.id} className="border-b border-[#EAEAEA] pb-6">
                      <p className="text-[#C59B27] tracking-widest">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                        {r.verified_purchase && <span className="ml-3 text-[10px] text-[#1A3C34] font-sans font-semibold uppercase tracking-wider">Verified Purchase</span>}
                      </p>
                      {r.title && <p className="font-semibold mt-2">{r.title}</p>}
                      <p className="mt-1">{r.body}</p>
                      <p className="text-xs text-[#8E8E93] mt-2">— {r.name}</p>
                      {r.reply && <p className="mt-3 pl-4 border-l-2 border-[#C59B27] text-[#666666]"><strong className="text-[#111111]">TARISHQ:</strong> {r.reply}</p>}
                    </li>
                  ))}
                </ul>
              )}
              <form onSubmit={submitReview} className="bg-white border border-[#EAEAEA] p-6 mt-8" data-testid="review-form">
                <p className="font-serif text-xl mb-4">Write a Review</p>
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })}
                      data-testid={`review-star-${n}`} aria-label={`${n} stars`}>
                      <Star size={22} className={n <= reviewForm.rating ? "fill-[#C59B27] text-[#C59B27]" : "text-[#EAEAEA]"} />
                    </button>
                  ))}
                </div>
                <input value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                  placeholder="Your name" required data-testid="review-name" className="tq-input mb-3" />
                <input value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                  placeholder="Review title (optional)" data-testid="review-title" className="tq-input mb-3" />
                <textarea value={reviewForm.body} onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })}
                  placeholder="Share your experience…" required rows={3} data-testid="review-body" className="tq-input mb-4" />
                <button type="submit" disabled={reviewBusy} data-testid="review-submit" className="tq-btn-dark">
                  {reviewBusy ? "Submitting…" : "Submit Review"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="font-serif text-3xl mb-8">You May Also Like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {related.slice(0, 4).map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}
          </div>
        </section>
      )}

      <div className="lg:hidden fixed bottom-14 left-0 right-0 z-30 bg-white border-t border-[#EAEAEA] p-3 flex gap-2" data-testid="sticky-mobile-atc">
        <button onClick={() => buy(false)} disabled={!variant} data-testid="sticky-add-to-cart-btn" className="tq-btn-outline flex-1 !py-3">Add to Bag</button>
        <button onClick={() => buy(true)} disabled={!variant} data-testid="sticky-buy-now-btn" className="tq-btn-dark flex-1 !py-3">Buy Now</button>
      </div>
      <SizeChartModal open={chartOpen} onClose={() => setChartOpen(false)} chart={chart} pdf={size_chart_pdf} />
    </div>
  );
}
